import { useEffect } from "react";

const storagePrefix = "zentraqTableColumnWidths:";
const minColumnWidth = 72;

function getTableKey(table) {
  const classKey = String(table.className || "")
    .split(/\s+/)
    .filter(Boolean)
    .join(".");
  const headers = Array.from(table.querySelectorAll("thead th"))
    .map((th) => th.textContent.trim())
    .join("|");

  return `${classKey || "data-table"}:${headers}`;
}

function loadWidths(table) {
  try {
    return JSON.parse(localStorage.getItem(`${storagePrefix}${getTableKey(table)}`) || "{}");
  } catch {
    return {};
  }
}

function saveWidths(table, widths) {
  localStorage.setItem(`${storagePrefix}${getTableKey(table)}`, JSON.stringify(widths));
}

function ensureColgroup(table, columnCount) {
  let colgroup = table.querySelector("colgroup[data-resizable-columns='true']");

  if (!colgroup) {
    colgroup = document.createElement("colgroup");
    colgroup.dataset.resizableColumns = "true";
    table.insertBefore(colgroup, table.firstChild);
  }

  while (colgroup.children.length < columnCount) {
    colgroup.appendChild(document.createElement("col"));
  }

  while (colgroup.children.length > columnCount) {
    colgroup.removeChild(colgroup.lastChild);
  }

  return colgroup;
}

function applyColumnWidth(table, index, width) {
  const colgroup = ensureColgroup(table, table.querySelectorAll("thead th").length);
  const col = colgroup.children[index];

  if (!col) {
    return;
  }

  col.style.width = `${width}px`;
  col.style.minWidth = `${width}px`;
}

function setupTable(table) {
  const headers = Array.from(table.querySelectorAll("thead th"));

  if (headers.length === 0) {
    return;
  }

  table.classList.add("resizable-table");
  ensureColgroup(table, headers.length);
  const widths = loadWidths(table);

  headers.forEach((header, index) => {
    if (widths[index]) {
      applyColumnWidth(table, index, widths[index]);
    }

    if (header.querySelector(":scope > .column-resize-handle")) {
      return;
    }

    const handle = document.createElement("span");
    handle.className = "column-resize-handle";
    handle.title = "Spaltenbreite anpassen";
    header.appendChild(handle);

    handle.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = header.getBoundingClientRect().width;
      const nextWidths = { ...loadWidths(table) };

      document.body.classList.add("column-resize-active");
      handle.setPointerCapture?.(event.pointerId);

      const onPointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          minColumnWidth,
          Math.round(startWidth + moveEvent.clientX - startX)
        );
        nextWidths[index] = nextWidth;
        applyColumnWidth(table, index, nextWidth);
      };

      const onPointerUp = () => {
        saveWidths(table, nextWidths);
        document.body.classList.remove("column-resize-active");
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
    });
  });
}

export default function ResizableTables() {
  useEffect(() => {
    const setupAllTables = () => {
      document.querySelectorAll("table.data-table").forEach(setupTable);
    };

    setupAllTables();
    const observer = new MutationObserver(setupAllTables);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return null;
}
